import { FunctionComponent, useCallback, memo } from 'react';
import { IntlShape } from 'react-intl';
import { useFormatMessage } from '../../i18n-review/client/Msg';
import { usePageContext } from 'vike-react/usePageContext';
import { AutocompleteHandle } from '../Autocomplete';
import { Logo } from '../navigation/Logo';
import { SearchNavbar } from './SearchNavbar';
import { TopFilters } from '../filters/TopFilters';
import { useSearchFilters, useSearchQuery, useSearchResults } from '../../contexts/SearchContext';
import { useFacets } from '../../contexts/FacetsContext';
import { getLocaleFromPathname, normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import { CORE_HEADER_STICKY_SRP, CORE_NAV_ROW_BORDER } from '../../constants/corePublicTheme';
import { AccountPill } from '../navigation/nav-live-index/AccountPill';
import { BarsMenuButton } from '../navigation/nav-live-index/BarsMenuButton';
import { LanguageSelector } from '../navigation/LanguageSelector';
import {
  countAppliedFilterValues,
  LOCATION_FILTER_ATTRIBUTES,
} from '../navigation/nav-live-index/applied-filter-count';
import { useSearchSort } from '../../contexts/SearchContext';
import { tapeMessages } from '../filters/tape-messages';
import {
  resolveLocationSegmentValue,
  formatLocationSegmentLabel,
  resolveLocationOnlyLabel,
} from '../navigation/nav-live-index/location-segment-label';
import { IndexStrip } from '../navigation/nav-live-index/IndexStrip';
import { MobileIndexSubtitle } from '../navigation/nav-live-index/MobileIndexSubtitle';
import type { MenuPulse } from '../../server/routes/menu-pulse';
import { useMenuPulse } from '../navigation/menu/useMenuPulse';
import { SearchFiltersChip } from '../navigation/nav-live-index/SearchBarParts';

interface DesktopFilterCategoriesProps {
  intl: IntlShape;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  isAllFiltersModalOpen?: boolean;
  setIsAllFiltersModalOpen?: (open: boolean) => void;
  /** Which rail section AllFiltersModal is currently showing (null while closed), so the
   * Location button only highlights when that's genuinely the tab in view. */
  allFiltersModalSection?: string | null;
  clearSearch: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
  user?: any;
  closeAutocomplete?: () => void;
  autocompleteRef?: React.RefObject<AutocompleteHandle | null>;
  onAutocompleteOpen?: () => void;
}

const DesktopFilterCategories: FunctionComponent<DesktopFilterCategoriesProps> = ({
  intl,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isAllFiltersModalOpen,
  setIsAllFiltersModalOpen,
  allFiltersModalSection,
  clearSearch,
  user,
  closeAutocomplete,
  autocompleteRef,
  onAutocompleteOpen,
}) => {
  const formatMessage = useFormatMessage();
  const { filters } = useSearchFilters();
  const pageContext = usePageContext();
  const pathname = (pageContext as any)?.urlPathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  const locale = normalizeSupportedLocale(
    pageContext?.locale ?? intl?.locale ?? getLocaleFromPathname(pathname) ?? 'en',
  );
  // Phase 3 item 3.2: server-rendered figures for IndexStrip/MobileIndexSubtitle's first paint.
  const initialPulse = (pageContext as { navPulse?: MenuPulse | null } | undefined)?.navPulse ?? null;
  // Sage search bar (2026-09-28): the forest count button shows the SRP's own current result
  // count, and the narrowing line compares it with all open jobs (the navbar's shared pulse).
  const barPulse = useMenuPulse(locale, true, initialPulse);
  const { results: barResults } = useSearchResults();
  const barCount = typeof barResults?.nbHits === 'number' ? barResults.nbHits : null;

  const filtersObj = typeof filters === 'object' && filters ? (filters as Record<string, string[]>) : {};
  const appliedFilterCount = countAppliedFilterValues(filtersObj);
  const nonLocationFilterCount = countAppliedFilterValues(filtersObj, LOCATION_FILTER_ATTRIBUTES);
  // Strip mode (no filters) still tells the visitor when the ordering is not the default, with
  // the same words the tape uses beside "Clear all" once filters are applied.
  const { sort: currentSort } = useSearchSort();
  const sortNote =
    currentSort === 'date'
      ? formatMessage(tapeMessages.sortNewest)
      : currentSort === 'salary'
        ? formatMessage(tapeMessages.sortSalary)
        : null;
  // A facet excluded via the SRP negate-filter UI is a real, results-changing filter
  // (stored as `exclude_<attribute>`) and must count as "active" here too, or the location
  // button misleadingly shows inactive while an exclusion is applied.
  const isExcluded = (key: string) => !!filtersObj[`exclude_${key}`]?.length;
  const locationHasFilters = !!(
    filtersObj.location?.length ||
    filtersObj.regionTags?.length ||
    filtersObj.remoteness?.length ||
    isExcluded('location') ||
    isExcluded('regionTags')
  );
  const locationHasExcluded = isExcluded('location');
  // Phase 1 review fix F3: the fused search bar's location segment shows the chosen place's (or
  // remoteness's) actual name instead of the plain word "Location" once a value is applied.
  const locationSegmentLabel = formatLocationSegmentLabel(
    resolveLocationSegmentValue(filtersObj, locale),
  );
  // Round 10 (user feedback): the search panel's own location pill (SearchNavbar's
  // panelLocationLabel prop, rendered only inside the open panel, not the closed bar's segment
  // above) shows LOCATION only, never remoteness, and defaults to "Anywhere" rather than the bare
  // word "Location".
  const panelLocationAnywhereWord = formatMessage({
    id: 'search.refinements.locationAnywhere',
    defaultMessage: 'Anywhere',
    description: "Search panel's own location pill label when no location filter is set (remoteness does not count)",
  });
  const panelLocationLabel = resolveLocationOnlyLabel(filtersObj, locale, panelLocationAnywhereWord);
  const panelLocationIsSet = !!filtersObj.regionTags?.length;

  const { query } = useSearchQuery();
  const { facetsData } = useFacets();
  const locationItems = facetsData.location || [];

  // Filter location facets to only show Finland, Remote, or Global locations (same allow/deny
  // list TopFilters and the location filter modal already use: kept local to this component
  // since it's the one place that needs the *filtered* list, not the raw facet response).
  const filterLocationFacets = useCallback(
    (items: Array<{ value: string; label: string; count: number }>) => {
      const allowedKeywords = [
        'finland',
        'suomi',
        'finländ',
        'remote',
        'etätyö',
        'etä',
        'distans',
        'fjärr',
        'global',
        'maailmanlaajuinen',
        'världsomfattande',
      ];
      const excludedPatterns = [
        'remote - united states',
        'etätyö - yhdysvallat',
        'etä - yhdysvallat',
        'distans - usa',
        'fjärr - usa',
        'distans - förenta staterna',
        'fjärr - förenta staterna',
      ];
      const activeLocationValues = filtersObj.location || [];
      return items.filter((item) => {
        if (activeLocationValues.includes(item.value)) return true;
        const lowerValue = item.value.toLowerCase();
        const lowerLabel = item.label.toLowerCase();
        if (excludedPatterns.some((pattern) => lowerValue.includes(pattern) || lowerLabel.includes(pattern)))
          return false;
        return allowedKeywords.some((keyword) => lowerValue.includes(keyword) || lowerLabel.includes(keyword));
      });
    },
    [filtersObj.location],
  );
  const filteredLocationItems = filterLocationFacets(locationItems);

  // Opens the Advanced Filters modal straight to its Location tab, rather than the standalone
  // location popover: the modal is now the single place location filtering lives, this button is
  // just a shortcut into it. AllFiltersModal reads its initial tab from the same localStorage key
  // it already persists "last section shown" under, so writing it here before opening is enough.
  const handleLocationClick = useCallback(() => {
    closeAutocomplete?.();
    if (typeof window !== 'undefined') {
      localStorage.setItem('allFiltersModal.openSection', 'location');
    }
    setIsAllFiltersModalOpen?.(true);
  }, [closeAutocomplete, setIsAllFiltersModalOpen]);

  return (
    <header className={CORE_HEADER_STICKY_SRP} role="banner">
      {/* Top row: Logo + Searchbar (as wide as possible) + Advanced Filters + Hamburger Menu (lg and above) */}
      <nav
        className={`hidden lg:flex items-center gap-3 px-4 xl:px-20 md:px-10 h-[58px] py-0 ${CORE_NAV_ROW_BORDER}`}
        aria-label="Main navigation"
      >
        {/* The menu button leads the row; the menu opens from the left (owner decision 2026-11-02). */}
        <span data-jc-menu-button className="contents">
          <BarsMenuButton
            onClick={() => {
              const willOpen = !(isMobileMenuOpen ?? false);
              setIsMobileMenuOpen?.(willOpen);
              if (willOpen && isAllFiltersModalOpen) {
                setIsAllFiltersModalOpen?.(false);
              }
            }}
            ariaLabel={formatMessage({ id: 'navigation.menu', defaultMessage: 'Menu' })}
          />
        </span>
        {/* Left side: Logo + Searchbar. The Where chip (location) and the filters chip (Advanced
            Filters) both live inside the search bar itself, not as separate buttons in this row. */}
        <SearchNavbar
          intl={intl}
          formatMessage={formatMessage}
          clearSearch={clearSearch}
          autocompleteRef={autocompleteRef}
          onAutocompleteOpen={onAutocompleteOpen}
          onLocationClick={handleLocationClick}
          isLocationActive={!!isAllFiltersModalOpen && allFiltersModalSection === 'location'}
          locationHasFilters={locationHasFilters}
          locationHasExcluded={locationHasExcluded}
          locationLabel={locationSegmentLabel ?? undefined}
          panelLocationLabel={panelLocationLabel}
          panelLocationIsSet={panelLocationIsSet}
          count={barCount}
          countTotal={barPulse?.openJobs ?? null}
          filtersSegment={
            <SearchFiltersChip
              onClick={() => {
                closeAutocomplete?.();
                // The Where chip beside it covers location, so this opens straight to the job
                // filters (field, role level, job type...), same localStorage key as location.
                if (typeof window !== 'undefined') {
                  try {
                    localStorage.setItem('allFiltersModal.openSection', 'job');
                  } catch {
                    // Storage blocked: the modal opens on its last-used section instead.
                  }
                }
                setIsAllFiltersModalOpen?.(true);
              }}
              ariaLabel={formatMessage({
                id: 'search.refinements.advancedFilters.aria',
                defaultMessage: 'Advanced Filters',
                description: 'Accessibility label for the button that opens the full filters modal',
              })}
              count={nonLocationFilterCount}
            />
          }
        />

        <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
          <LanguageSelector intl={intl} formatMessage={formatMessage} locale={locale} sizeVariant="compact40" />
          <span className="w-[10px]" aria-hidden />
          <AccountPill locale={locale} />

        </div>
      </nav>

      {/* Second row (TEC navbar redesign Phase 2 item 2.1/2.4): the index strip when no filter is
          applied, the filter tape once one is (both live inside TopFilters/IndexStrip; this just
          picks which one mounts). */}
      {appliedFilterCount === 0 ? (
        <>
          <IndexStrip
            locale={locale}
            pathname={pathname}
            formatMessage={formatMessage}
            initialPulse={initialPulse}
            rightSlot={
              <>{sortNote ? <span className="text-xs text-jc-ink-secondary whitespace-nowrap mr-3">{sortNote}</span> : null}</>
            }
          />
          {/* IndexStrip is desktop-only (see IndexStrip.tsx) and there is no other mobile top bar
              on the SRP (DesktopFilterCategories' row one is `hidden lg:flex`; mobile chrome is
              MobileBottomNav at the bottom only): without this, an unfiltered mobile SRP would
              show no row two at all, a real regression from the classic pill row's always-visible
              suggested-filter chips. This carries the same live figures the spec's mobile
              top-bar subtitle (Phase 1, NavigationNavbar only) shows elsewhere, as the closest
              equivalent; see the Phase 2 report's deviations section.
              Review fix P3: the classic mobile row carried the icon logo as the only home link on
              the mobile SRP; this row lacked it. Since the design-system pass it is the full
              wordmark (Logo `compact`, 44px tap height in a 48px row) with only "new this week" on
              the right: the SRP head right below already gives the open-jobs total. */}
          <div className="flex lg:hidden items-center h-12 px-4 bg-jc-surface gap-3">
            <Logo clearSearch={clearSearch} variant="compact" locale={locale} className="h-11 -ml-1 pr-1 flex-shrink-0" />
            <span className="ml-auto min-w-0">
              <MobileIndexSubtitle locale={locale} formatMessage={formatMessage} initialPulse={initialPulse} only="newThisWeek" />
            </span>
          </div>
        </>
      ) : (
        <TopFilters
          intl={intl}
          formatMessage={formatMessage}
          clearSearch={clearSearch}
          facetsData={{
            ...facetsData,
            location: filteredLocationItems,
          }}
          filtersObj={filtersObj}
          query={query}
        />
      )}
    </header>
  );
};

// Memoize DesktopFilterCategories to prevent unnecessary re-renders
export const DesktopFilterCategoriesMemo = memo(DesktopFilterCategories);
